import React, {
	forwardRef,
	type MouseEvent,
	type KeyboardEvent,
	type PropsWithChildren,
} from 'react';
import { styled } from '@compiled/react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import Button from '@atlaskit/button';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';

const ConditionalWrapper = componentWithCondition(
	() =>
		fg('empanada_nin_covnert_share_to_entrypoint') || fg('empanada_nin_convert_apps_to_entrypoint'),
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
	({ vcId, children }: PropsWithChildren<{ vcId?: string }>) => (
		<Box xcss={buttonWrapperStyles} data-vc={`${vcId}-button${__SERVER__ ? '-ssr' : ''}`}>
			{children}
		</Box>
	),
);

export type FilterButtonProps = {
	isSelected?: boolean;
	isDisabled?: boolean;
	text: string;
	label: string;
	testId?: string;
	/**
	 * @deprecated This prop was for use with React Loosely Lazy. We should have no more usages with RLL after the cleanup of:
	 * - empanada_nin_covnert_share_to_entrypoint
	 * - empanada_nin_convert_apps_to_entrypoint
	 * - visual-refresh-headers-buttons
	 */
	vcId?: string;
	customTriggerProps?: TriggerProps; // when using as a custom trigger for some popups (like Share),
	onClick?: (e: MouseEvent | KeyboardEvent) => void;
};

export const FilterButton = forwardRef<HTMLElement, FilterButtonProps>(
	(
		{
			isSelected,
			onClick,
			testId,
			vcId,
			isDisabled,
			text,
			label,
			customTriggerProps,
		}: FilterButtonProps,
		ref,
	) => {
		const mergedRefs = useMergeRefs(ref, customTriggerProps?.ref ?? null);

		return (
			<ConditionalWrapper vcId={vcId}>
				<Button
					isSelected={isSelected}
					onClick={onClick}
					{...(fg('empanada_nin_covnert_share_to_entrypoint') ? {} : { ref })} // Moving ref to end and merging with custom trigger props ref
					testId={testId}
					isDisabled={isDisabled}
					iconAfter={
						<IconWrapper>
							<ChevronDownIcon label={label} color="currentColor" size="small" />
						</IconWrapper>
					}
					data-vc={
						fg('empanada_nin_covnert_share_to_entrypoint') ||
						fg('empanada_nin_convert_apps_to_entrypoint')
							? `${vcId}-button${__SERVER__ ? '-ssr' : ''}`
							: undefined
					}
					{...customTriggerProps}
					{...(fg('empanada_nin_covnert_share_to_entrypoint') ? { ref: mergedRefs } : {})}
				>
					{text}
				</Button>
			</ConditionalWrapper>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span({
	display: 'inline-flex',
	alignItems: 'center',
	/* Offset inner icon padding to match designs, as changing icon size to small (contrary
    /* to what Atlaskit docs say) does reduce icon shape size, and we don't want that */
	marginRight: token('space.negative.075'),
});

const buttonWrapperStyles = xcss({
	position: 'relative',
});
